import React, { useEffect, useRef, useState } from "react";
import * as d3 from "d3";
import "../style.css";
import LabelText from "../../../ui/LabelText";
import LabelNumber from "../../../styled-components/LabelNumber";
import styled from "styled-components";
import { useTheme } from "../../../contexts/ThemeContext";
import { IDonutRingData } from "../../../types/IClubDetail";
import EmptyDataButton from "../../../ui/EmptyDataButton";
import LargeModal from "../../../ui/LargeModal";

const TableRow = styled.div`
  border-bottom: 1px solid ${({ theme }) => theme.playerDataBorder};
`;

interface DonutRingChartProps {
  data: IDonutRingData[] | undefined;
  total: string | undefined;
  type?: string;
}

const DonutRingChart: React.FC<DonutRingChartProps> = ({ data, total, type }) => {
  const isAllNull = data?.every((item) => item.text === null);
  const isPartialNull = data?.some((item) => item.text === null) && !isAllNull;
  const { theme } = useTheme();
  const svgRef = useRef<SVGSVGElement>(null); // UseRef with SVG element type
  const [isLargeModalOpen, setLargeModalIsOpen] = useState(false);
  const [isInferred, setIsInferred] = useState<boolean>(false);

  useEffect(() => {
    if (data !== undefined) {
      const isInferred = data?.filter((d) => d.Inferred === true).length > 0 ? true : false;
      setIsInferred(isInferred);
    }
  }, [data]);

  const toggleLargeModal = () => {
    setLargeModalIsOpen(!isLargeModalOpen);
  };

  const modalData = {
    headerTitle: "Data not provided by club",
    actionTag: " ",
    items: [
      {
        subTitle: " ",
        description:
          "Leeds United are situated in a country who’s laws to not require them to make public the data we’d need to fully calculate this sum.",
      },
    ],
  };

  const inferredData = {
    headerTitle: "Inferred calculation",
    actionTag: " ",
    items: [
      {
        subTitle: " ",
        description:
          "Leeds United are situated in a country who’s laws to not require them to make this data public, and so we have used the delta between the total assets and the sum of the rest of the available assets to infer this number.",
      },
    ],
  };

  useEffect(() => {
    if (!svgRef.current) return;
    const svg = d3.select(svgRef.current);
    d3.select(svgRef.current).selectAll("*").remove();
    // Set up your SVG container
    const width = 342;
    const height = 302;
    svg.attr("width", width).attr("height", height);
    // Create an arc generator with both-side curves
    const radius = Math.min(width, height) / 2;
    const arc = d3
      .arc<any>() // Specify the data type for arc
      .innerRadius(radius - 15) // Inner radius for the donut
      .outerRadius(radius)
      .cornerRadius(25); // Set corner radius for all segments
    // Create a pie layout
    const pie = d3
      .pie<any>() // Specify the data type for pie
      .value((d) => d.value)
      .padAngle(0.01); // Add padding between segments
    const dataItems = data ?? []; // Ensure data is an array
    // Draw the gray circle outline behind the donut chart
    svg
      .append("circle")
      .attr("cx", width / 2)
      .attr("cy", height / 2)
      .attr("r", radius - 8)
      .attr("fill", "none")
      .attr("stroke", theme.playerDataBorder)
      .attr("stroke-width", 2);
    // Draw the segments
    const g = svg.append("g").attr("transform", `translate(${width / 2}, ${height / 2})`);
    const tooltip = d3
      .select("body")
      .append("div")
      .style("position", "fixed")
      .style("z-index", 999)
      .style("visibility", "hidden")
      .style("background-color", theme.secondaryButtonBackground)
      .style("color", theme.color)
      .style("border", "1px solid ${theme.secondaryButtonBackground}")
      .style("padding", "8px")
      .style("border-radius", "4px")
      .style("box-shadow", "0px 0px 6px rgba(0, 0, 0, 0.1)");
    let tooltipRef: NodeJS.Timeout;
    g.selectAll("path")
      .data(pie(dataItems))
      .enter()
      .append("path")
      .attr("d", arc)
      .attr("fill", (d) => d.data.color)
      .style("cursor", "pointer")
      .on("mouseover", (event, d) => {
        tooltipRef = setTimeout(() => {
          tooltip.style("visibility", "visible").text(`${d.data.label}: €${d.data.text}`);
        }, 500);
      })
      .on("mousemove", (event) => {
        tooltip.style("left", `${event.clientX - 50}px`).style("top", `${event.clientY - 50}px`);
      })
      .on("mouseout", () => {
        clearTimeout(tooltipRef);
        tooltip.style("visibility", "hidden");
      });
    function getTextForType(type: string | undefined) {
      if (type === "assets") {
        return "Total Assets";
      } else if (type === "cost") {
        return "Total Costs";
      } else if (type === "liabilities") {
        return "Total Liabilities";
      } else {
        return "Total Revenues";
      }
    }
    // Add central text
    if (total !== null) {
      svg
        .append("text")
        .attr("x", width / 2)
        .attr("y", height / 2 - 10)
        .attr("text-anchor", "middle") // Center the text
        .style("font-family", "Aeonik") // Set font family to Aeonik
        .style("font-size", "20px") // Set font size
        .style("font-weight", 700)
        .style("fill", theme.playerColor) // Set text color
        .text(getTextForType(type));
      // Add central text
      svg
        .append("text")
        .attr("x", width / 2)
        .attr("y", height / 2 + 25)
        .attr("text-anchor", "middle") // Center the text
        .style("font-family", "DM Mono") // Set font family to Aeonik
        .style("font-size", "32px") // Set font size
        .style("font-weight", 500)
        .style("fill", theme.playerColor) // Set text color
        .text(total !== undefined ? `€${total}` : "");
    }
    if (isAllNull || isPartialNull || isInferred) {
      // Add central text
      svg
        .append("rect")
        .attr("class", "rect")
        .attr("x", width / 4.5)
        .attr("y", height / 2.2 + (isPartialNull || isInferred ? 45 : 0))
        .attr("text-anchor", "middle")
        .attr("width", 190)
        .attr("height", 35)
        .attr("rx", 2)
        .attr("ry", 2)
        .style("stroke", theme.emptyDataBorder)
        .style("cursor", "pointer")
        .style("fill", theme.playerDataBorder);
      // Set text color
      const displayText = isAllNull
        ? "Data not provided by club"
        : isInferred
        ? "Some calculations are inferred"
        : "Full Data not provided by club";
      svg
        .append("text")
        .attr("x", width / (isPartialNull || isInferred ? 2.1 : 2.2))
        .attr("y", height / 2.2 + (isPartialNull || isInferred ? 65 : 21))
        .attr("text-anchor", "middle") // Center the text
        .style("font-family", "Aeonik") // Set font family to Aeonik
        .style("font-size", "12px") // Set font size
        .style("font-weight", 500)
        .style("cursor", "pointer")
        .style("fill", theme.playerColor) // Set text color
        .text(displayText);
      // Calculate the position for the icon
      const iconX = width / (isPartialNull || isInferred ? 2.1 : 2.3) + 85; // Adjust the X position as needed
      const iconY = height / 2.2 + (isPartialNull || isInferred ? 52 : 8); // Adjust the Y position as needed
      // Append the icon (using SVG path data for HelpOutlineIcon)
      svg
        .append("path")
        .attr(
          "d",
          "M 11 18 h 2 v -2 h -2 Z m 1 -16 C 6.48 2 2 6.48 2 12 s 4.48 10 10 10 s 10 -4.48 10 -10 S 17.52 2 12 2 m 0 18 c -4.41 0 -8 -3.59 -8 -8 s 3.59 -8 8 -8 s 8 3.59 8 8 s -3.59 8 -8 8 m 0 -14 c -2.21 0 -4 1.79 -4 4 h 2 c 0 -1.1 0.9 -2 2 -2 s 2 0.9 2 2 c 0 2 -3 1.75 -3 5 h 2 c 0 -2.25 3 -2.5 3 -5 c 0 -2.21 -1.79 -4 -4 -4"
        )
        .attr("transform", `translate(${iconX}, ${iconY})`)
        .attr("transform", `translate(${iconX}, ${iconY}) scale(0.7)`) // Scale down by 50%
        .style("fill", theme.playerColor)
        .on("click", (event, d) => {
          setLargeModalIsOpen(!isLargeModalOpen); // Example alert, you can use any property from the data
        });
    }
  }, [theme, data, total, isInferred]); // Add total to dependencies

  return (
    <>
      <LargeModal toggleLargeModal={toggleLargeModal} isOpen={isLargeModalOpen} data={isInferred ? inferredData : modalData} />
      <div className={type === "assets" || type === "liabilities" ? "flex-container" : "donutchart-container"}>
        <svg ref={svgRef}></svg>
        <div className="stat-table-container">
          <div className="stat-table-column">
            {data?.map((d, index) => (
              <TableRow key={index} className="stat-table-row">
                <span
                  style={{
                    backgroundColor: d.color,
                    width: 12,
                    height: 12,
                    color: d.color,
                    borderRadius: "50%",
                    display: "inline-block",
                    marginRight: 8,
                  }}
                ></span>
                <LabelText className="selected-zone-name">{d.label}</LabelText>
                {d.Inferred === true && <LabelText className="secondary-cta-text">Inferred</LabelText>}
                {isAllNull ? (
                  <LabelNumber className="chart-content-number">--</LabelNumber>
                ) : isPartialNull && d.text === null ? (
                  <div style={{ marginLeft: "auto" }}>
                    <EmptyDataButton label="Data not provided by club" />
                  </div>
                ) : (
                  <LabelNumber className="chart-content-number">{d.text !== "Unknown" ? `€${d.text}` : d.text}</LabelNumber>
                )}
              </TableRow>
            ))}
          </div>
        </div>
      </div>
    </>
  );
};

export default DonutRingChart;
